<script setup lang="ts">
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useRouter } from 'vue-router'

import type { OrganizationDuplicateMatch } from '@/composables/relations/useOrganizationDuplicateCheck'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import { getOrganizationDetailRoute } from '@/routers/routes/mainRoutes/routeRelationships/routeOrganizationDetail'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore'
import { translationFunctionBackend, translationFunctionDefault } from '@/utilities/translations'

defineProps<{
  matches: OrganizationDuplicateMatch[]
}>()

const tfBackend = translationFunctionBackend()
const tfDefault = translationFunctionDefault()

const router = useRouter()
const { closeCreateRelationOverlay } = useCreateRelationSlideInStore()

const openMatch = async (match: OrganizationDuplicateMatch): Promise<void> => {
  closeCreateRelationOverlay()
  await router.push({
    name: getOrganizationDetailRoute(match.relationType).name,
    params: { id: match.id },
  })
}
</script>

<template>
  <div
    class="flex gap-3 rounded-lg border border-lightning_yellow-400 bg-lightning_yellow-50 p-4"
    :data-testid="TEST_IDS_CREATE.createOrganizationForm.duplicateWarning"
  >
    <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-lightning_yellow-500" />
    <div class="flex flex-col gap-2">
      <p class="text-sm font-medium text-black-900">
        {{ tfDefault('createOrganization.duplicateWarningTitle') }}
      </p>
      <p class="text-sm text-black-700">
        {{ tfDefault('createOrganization.duplicateWarningMessage') }}
      </p>
      <ul class="flex flex-col gap-1">
        <li
          v-for="match in matches"
          :key="match.id"
          class="text-sm text-black-900"
          :data-testid="TEST_IDS_CREATE.createOrganizationForm.duplicateWarningItem"
        >
          <button
            class="font-medium text-lochmara-600 hover:underline"
            type="button"
            @click="() => openMatch(match)"
          >
            {{ match.organizationName }}
          </button>
          <span class="text-black-500">
            · {{ tfBackend(`relationType.${match.relationType}`) }} · {{ match.id }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
